import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function SafetyPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const phases = [
    {
      id: 1,
      phase: 'Preflight',
      icon: 'ri-file-list-3-fill',
      color: '#00FFD1',
      status: 'Ground Operations',
      description: 'Review aircraft documentation, weather, and flight planning before engine start.',
      howToUse: 'Use WingmanFix to review your route, expected clearances, and aircraft performance calculations. Cross-check all data with your AFM/POH.',
      dos: [
        'Review clearance expectations and routing before calling clearance delivery',
        'Use the app to practice readback phraseology on the ground',
        'Verify all performance calculations against your POH',
        'Set up the app mount and test audio before engine start'
      ],
      donts: [
        'Don\'t rely solely on WingmanFix for weight & balance or performance data',
        'Don\'t skip your normal preflight checklist procedures',
        'Don\'t use the app as a substitute for proper flight planning',
        'Don\'t assume AI-generated suggestions replace pilot judgment'
      ]
    },
    {
      id: 2,
      phase: 'Taxi & Runup',
      icon: 'ri-roadster-fill',
      color: '#B4FF39',
      status: 'Low Workload',
      description: 'Monitor ATC communications and complete runup checklist while maintaining situational awareness.',
      howToUse: 'Let WingmanFix transcribe taxi instructions and monitor radio traffic. Use voice commands only when stopped and clear of active taxiways.',
      dos: [
        'Use audio transcription to capture complex taxi instructions',
        'Review departure clearance summary while stopped at runup area',
        'Verify runway assignment and departure frequency before takeoff',
        'Keep eyes outside during all taxi operations'
      ],
      donts: [
        'Don\'t interact with the app while the aircraft is moving',
        'Don\'t trust transcription accuracy without verifying with ATC',
        'Don\'t use the app during critical runup checklist items',
        'Don\'t let the device distract you from traffic or obstacles'
      ]
    },
    {
      id: 3,
      phase: 'Departure & Climb',
      icon: 'ri-flight-takeoff-fill',
      color: '#00A3E0',
      status: 'High Workload',
      description: 'Execute takeoff, establish climb, and comply with departure clearance while managing aircraft control.',
      howToUse: 'Minimize app interaction during this critical phase. Use only for quick clearance reference if needed. Voice commands should be brief and only when workload permits.',
      dos: [
        'Glance at clearance summary only when aircraft is stabilized in climb',
        'Use voice readback feature only above pattern altitude',
        'Keep primary focus on aircraft control and traffic avoidance',
        'Verify altitude and heading assignments before making changes'
      ],
      donts: [
        'Don\'t interact with the app during takeoff roll or initial climb',
        'Don\'t use voice commands below 1,000 feet AGL',
        'Don\'t rely on the app for altitude or heading information',
        'Don\'t let the app distract from sterile cockpit procedures'
      ]
    },
    {
      id: 4,
      phase: 'Cruise',
      icon: 'ri-compass-3-fill',
      color: '#FF6B6B',
      status: 'Moderate Workload',
      description: 'Maintain course, altitude, and situational awareness while monitoring weather and ATC communications.',
      howToUse: 'This is the best time to use WingmanFix features. Review flight progress, practice radio calls, and use coaching insights when workload is low.',
      dos: [
        'Use the app to review upcoming airspace and frequency changes',
        'Practice ATC phraseology with voice interaction features',
        'Review flight scoring and performance metrics',
        'Ask WingmanFix to explain procedures or regulations'
      ],
      donts: [
        'Don\'t become fixated on the app — maintain scan pattern',
        'Don\'t trust the app for navigation — use certified avionics',
        'Don\'t use the app during weather deviations or emergencies',
        'Don\'t let coaching features distract from primary flight duties'
      ]
    },
    {
      id: 5,
      phase: 'Approach & Landing',
      icon: 'ri-flight-land-fill',
      color: '#9D4EDD',
      status: 'High Workload',
      description: 'Execute approach procedures, configure aircraft, and land safely while complying with ATC clearances.',
      howToUse: 'Limit app use to essential clearance verification only. Focus on flying the aircraft and following published procedures. No voice interaction below 500 feet AGL.',
      dos: [
        'Glance at approach clearance summary only when stabilized',
        'Use the app to verify missed approach procedures before FAF',
        'Keep the app display minimal and non-distracting',
        'Prioritize aircraft control and landing configuration'
      ],
      donts: [
        'Don\'t interact with the app during final approach',
        'Don\'t use voice commands below 500 feet AGL',
        'Don\'t rely on the app for approach guidance or minimums',
        'Don\'t let the app delay a go-around decision'
      ]
    },
    {
      id: 6,
      phase: 'Post-Flight',
      icon: 'ri-bar-chart-box-fill',
      color: '#FFB800',
      status: 'Ground Operations',
      description: 'Secure aircraft, review flight performance, and debrief the flight for continuous improvement.',
      howToUse: 'This is when WingmanFix shines. Review your flight scoring, radio transcripts, pattern analysis, and coaching insights. Use this data to improve your next flight.',
      dos: [
        'Review flight scoring and identify areas for improvement',
        'Read through ATC transcript to catch missed communications',
        'Study coaching insights and recommended training areas',
        'Share flight replay with your CFI for debriefing'
      ],
      donts: [
        'Don\'t obsess over scores — focus on learning and improvement',
        'Don\'t assume the app caught every mistake or success',
        'Don\'t skip your normal post-flight inspection procedures',
        'Don\'t use the app as a substitute for instructor feedback'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        } border-b border-[#00FFD1]/20`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/2de32bf8-6894-4f4a-ba90-b9480aaa88cf.png"
              alt="WingmanFix Logo"
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold tracking-tight">WingmanFix</span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap">
              Features
            </Link>
            <Link to="/pricing" className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap">
              Pricing
            </Link>
            <Link to="/safety" className="text-sm text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap font-semibold">
              Safety
            </Link>
            <Link to="/terms" className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap">
              Terms
            </Link>
          </div>

          <button className="bg-[#00A3E0] hover:bg-[#0088BB] text-white px-6 py-2.5 rounded-full transition-all font-medium text-sm whitespace-nowrap cursor-pointer">
            Download App
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9283cc7794a43c6c937d6f6405246c96.jpg"
            alt="Aviation safety background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/85 via-[#0A1628]/80 to-[#0A1628]"></div>
          {/* Subtle grid overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(rgba(0,255,209,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,209,0.15) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          ></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          {/* Safety Badge */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/40 rounded-full px-5 py-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-fill text-[#00FFD1] text-base"></i>
              </div>
              <span className="text-[#00FFD1] text-sm font-semibold uppercase tracking-widest">Safety First</span>
            </div>
            <div className="flex items-center gap-2 bg-[#B4FF39]/10 border border-[#B4FF39]/40 rounded-full px-5 py-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-flight-takeoff-fill text-[#B4FF39] text-base"></i>
              </div>
              <span className="text-[#B4FF39] text-sm font-semibold uppercase tracking-widest">Pilot in Command</span>
            </div>
          </div>

          {/* Main Headline */}
          <h1 className="text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
            Fly Smarter.
            <br />
            <span className="text-[#00FFD1]">Stay Safe.</span>
          </h1>

          <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto mb-6 leading-relaxed">
            WingmanFix is a powerful training and situational awareness tool — but the cockpit always belongs to you.
            This guide shows you how to use it safely across every phase of flight.
          </p>

          <p className="text-base text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            Designed for student pilots, private pilots, and instrument-rated aviators who want to get the most out of WingmanFix without compromising safety or airmanship.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-16">
            <a
              href="#best-practices"
              className="bg-[#00FFD1] hover:bg-[#00E5BB] text-[#0A1628] px-10 py-4 rounded-lg transition-all font-bold text-base shadow-lg hover:shadow-[#00FFD1]/40 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              View Best Practices
            </a>
            <Link
              to="/terms"
              className="border-2 border-white/30 text-white hover:border-[#00FFD1] hover:text-[#00FFD1] px-10 py-4 rounded-lg transition-all font-semibold text-base cursor-pointer whitespace-nowrap"
            >
              Read Full Terms
            </Link>
          </div>

          {/* Safety Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: 'ri-shield-check-fill', value: '6', label: 'Flight Phases Covered', color: '#00FFD1' },
              { icon: 'ri-user-star-fill', value: 'PIC', label: 'Always in Command', color: '#B4FF39' },
              { icon: 'ri-book-open-fill', value: 'AFM', label: 'Always the Authority', color: '#00A3E0' },
              { icon: 'ri-error-warning-fill', value: '0', label: 'Certified Avionics Claims', color: '#FF6B6B' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-[#0D1B2E]/80 backdrop-blur-sm border border-white/10 rounded-xl p-5 text-center hover:border-[#00FFD1]/30 transition-all"
              >
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-2xl`} style={{ color: stat.color }}></i>
                </div>
                <div className="text-3xl font-extrabold mb-1" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll to explore</span>
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-arrow-down-line text-gray-500 text-lg"></i>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section id="best-practices" className="bg-[#1A0A0A] border-y border-red-500/30 py-5">
        <div className="max-w-5xl mx-auto px-6 flex items-start gap-4">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-fill text-red-400 text-2xl"></i>
          </div>
          <div>
            <p className="text-red-300 font-semibold text-sm mb-1 uppercase tracking-wider">Important Safety Notice</p>
            <p className="text-gray-300 text-sm leading-relaxed">
              WingmanFix is an <strong className="text-white">educational and advisory tool only</strong>. It is not FAA-certified avionics equipment and must never be used as a primary flight instrument. The Pilot in Command is solely responsible for the safe operation of the aircraft at all times. Always verify all information using your approved Aircraft Flight Manual (AFM) or Pilot Operating Handbook (POH).
            </p>
          </div>
        </div>
      </section>

      {/* Phase-by-Phase Best Practices */}
      <section className="py-24 bg-gradient-to-b from-[#0A1628] to-[#0D1B2E]">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-5 py-2 mb-6">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-flight-takeoff-fill text-[#00FFD1] text-base"></i>
              </div>
              <span className="text-[#00FFD1] text-sm font-semibold uppercase tracking-widest">Phase-by-Phase Guide</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Safety Best Practices
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Learn how to use WingmanFix safely and effectively during each phase of flight — from preflight planning to post-flight debrief.
            </p>
          </div>

          {/* Flight Phases */}
          <div className="space-y-6">
            {phases.map((phase, index) => (
              <div
                key={phase.id}
                className={`bg-[#0D1B2E]/60 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 ${
                  activePhase === phase.id ? 'border-[#00FFD1]/50 shadow-lg shadow-[#00FFD1]/10' : 'hover:border-white/20'
                }`}
              >
                {/* Phase Header - Clickable */}
                <button
                  onClick={() => setActivePhase(activePhase === phase.id ? null : phase.id)}
                  className="w-full px-8 py-6 flex items-center justify-between cursor-pointer text-left"
                >
                  <div className="flex items-center gap-6">
                    {/* Phase Number */}
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-xl font-bold text-lg"
                      style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </div>

                    {/* Phase Icon */}
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${phase.color}15` }}
                    >
                      <i className={`${phase.icon} text-2xl`} style={{ color: phase.color }}></i>
                    </div>

                    {/* Phase Info */}
                    <div>
                      <h3 className="text-2xl font-bold mb-1">{phase.phase}</h3>
                      <p className="text-sm text-gray-400">{phase.description}</p>
                    </div>
                  </div>

                  {/* Status Badge & Expand Icon */}
                  <div className="flex items-center gap-4">
                    <div
                      className="px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider"
                      style={{ backgroundColor: `${phase.color}20`, color: phase.color }}
                    >
                      {phase.status}
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i
                        className={`ri-arrow-${activePhase === phase.id ? 'up' : 'down'}-s-line text-xl text-gray-400 transition-transform`}
                      ></i>
                    </div>
                  </div>
                </button>

                {/* Expanded Content */}
                {activePhase === phase.id && (
                  <div className="px-8 pb-8 space-y-6 animate-fadeIn">
                    {/* How to Use WingmanFix */}
                    <div className="bg-[#0A1628]/60 border border-[#00FFD1]/20 rounded-xl p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-flash-fill text-[#00FFD1] text-lg"></i>
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-[#00FFD1] mb-2">How to Use WingmanFix</h4>
                          <p className="text-gray-300 text-sm leading-relaxed">{phase.howToUse}</p>
                        </div>
                      </div>
                    </div>

                    {/* DO's and DON'Ts Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* DO's */}
                      <div className="bg-[#0A1628]/40 border border-green-500/20 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                          </div>
                          <h4 className="text-lg font-bold text-green-400">DO</h4>
                        </div>
                        <ul className="space-y-3">
                          {phase.dos.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0 mt-2"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* DON'Ts */}
                      <div className="bg-[#0A1628]/40 border border-red-500/20 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-close-circle-fill text-red-400 text-xl"></i>
                          </div>
                          <h4 className="text-lg font-bold text-red-400">DON'T</h4>
                        </div>
                        <ul className="space-y-3">
                          {phase.donts.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                              <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-2"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quick Reference Card ── */}
      <section className="py-24 bg-[#060E1A]">
        <div className="max-w-6xl mx-auto px-6">

          {/* Section Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-[#B4FF39]/10 border border-[#B4FF39]/30 rounded-full px-5 py-2 mb-6">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-list-3-fill text-[#B4FF39] text-base"></i>
              </div>
              <span className="text-[#B4FF39] text-sm font-semibold uppercase tracking-widest">Quick Reference</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              At-a-Glance Safety Card
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Your one-page summary of how to use WingmanFix safely across every phase of flight. Print it, save it, know it.
            </p>
          </div>

          {/* Reference Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {[
              {
                phase: 'Preflight',
                number: '01',
                icon: 'ri-file-list-3-fill',
                color: '#00FFD1',
                status: 'Ground Ops',
                tip: 'Review expected clearances and practice readback phraseology before engine start.',
                rule: 'Always verify performance data against your POH — never rely solely on the app.',
                workload: 'low',
              },
              {
                phase: 'Taxi & Runup',
                number: '02',
                icon: 'ri-roadster-fill',
                color: '#B4FF39',
                status: 'Low Workload',
                tip: 'Use audio transcription to capture taxi instructions. Interact only when stopped.',
                rule: 'Eyes outside at all times while the aircraft is moving.',
                workload: 'low',
              },
              {
                phase: 'Departure & Climb',
                number: '03',
                icon: 'ri-flight-takeoff-fill',
                color: '#00A3E0',
                status: 'High Workload',
                tip: 'Minimize app interaction. Glance at clearance summary only when stabilized in climb.',
                rule: 'No voice commands below 1,000 ft AGL. Sterile cockpit applies.',
                workload: 'high',
              },
              {
                phase: 'Cruise',
                number: '04',
                icon: 'ri-compass-3-fill',
                color: '#FF6B6B',
                status: 'Moderate Workload',
                tip: 'Best time to use WingmanFix. Review insights, practice radio calls, check routing.',
                rule: 'Maintain your scan — never become fixated on the app display.',
                workload: 'moderate',
              },
              {
                phase: 'Approach & Landing',
                number: '05',
                icon: 'ri-flight-land-fill',
                color: '#9D4EDD',
                status: 'High Workload',
                tip: 'Essential clearance reference only. No voice interaction below 500 ft AGL.',
                rule: 'Never let the app delay a go-around decision. Fly the aircraft first.',
                workload: 'high',
              },
              {
                phase: 'Post-Flight',
                number: '06',
                icon: 'ri-bar-chart-box-fill',
                color: '#FFB800',
                status: 'Ground Ops',
                tip: 'Review flight scoring, ATC transcripts, and coaching insights for your debrief.',
                rule: 'App scores supplement — never replace — feedback from a qualified CFI.',
                workload: 'low',
              },
            ].map((item) => (
              <div
                key={item.phase}
                className="relative bg-[#0D1B2E] border border-white/10 rounded-2xl p-6 flex flex-col gap-4 hover:border-white/25 transition-all group overflow-hidden"
              >
                {/* Top accent line */}
                <div
                  className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl"
                  style={{ backgroundColor: item.color }}
                ></div>

                {/* Header row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${item.color}18` }}
                    >
                      <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: item.color }}>
                        {item.number}
                      </span>
                      <h3 className="text-base font-bold leading-tight">{item.phase}</h3>
                    </div>
                  </div>
                  {/* Workload badge */}
                  <span
                    className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap ${
                      item.workload === 'high'
                        ? 'bg-red-500/15 text-red-400'
                        : item.workload === 'moderate'
                        ? 'bg-yellow-500/15 text-yellow-400'
                        : 'bg-green-500/15 text-green-400'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>

                {/* Tip */}
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-flash-fill text-[#00FFD1] text-sm"></i>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{item.tip}</p>
                </div>

                {/* Safety Rule */}
                <div className="flex items-start gap-2.5 bg-[#0A1628]/60 border border-red-500/15 rounded-lg px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-fill text-red-400 text-sm"></i>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.rule}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── Safety Disclaimer Banner ── */}
          <div className="relative bg-gradient-to-br from-[#1A0A0A] to-[#0D0A1A] border border-red-500/40 rounded-2xl p-8 mb-12 overflow-hidden">
            {/* Background glow */}
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-red-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-red-500/5 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-6">
              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-red-500/15 border border-red-500/30 flex-shrink-0">
                <i className="ri-shield-cross-fill text-red-400 text-3xl"></i>
              </div>

              {/* Text */}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-300 mb-2 uppercase tracking-wide">
                  Pilot in Command — Always
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-3">
                  WingmanFix is an <strong className="text-white">educational and advisory tool only</strong>. It is <strong className="text-red-300">not FAA-certified avionics</strong> and must never be used as a primary flight instrument, navigation system, or operational decision-making authority. All suggestions, summaries, and AI-generated outputs may contain errors.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  The <strong className="text-white">Pilot in Command is solely and fully responsible</strong> for the safe operation of the aircraft, compliance with ATC instructions, weather decisions, performance calculations, and all emergency procedures. Always verify information using your approved <strong className="text-white">AFM / POH</strong> and certified aircraft systems.
                </p>
              </div>

              {/* Badges column */}
              <div className="flex flex-col gap-3 flex-shrink-0">
                {[
                  { icon: 'ri-user-star-fill', label: 'PIC Responsibility', color: '#FF6B6B' },
                  { icon: 'ri-book-open-fill', label: 'AFM / POH Authority', color: '#FFB800' },
                  { icon: 'ri-close-circle-fill', label: 'Not Certified Avionics', color: '#9D4EDD' },
                ].map((b) => (
                  <div
                    key={b.label}
                    className="flex items-center gap-2 bg-[#0A1628]/60 border border-white/10 rounded-lg px-4 py-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${b.icon} text-base`} style={{ color: b.color }}></i>
                    </div>
                    <span className="text-xs font-semibold text-gray-300 whitespace-nowrap">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── CTA Row ── */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <a
              href="#best-practices"
              className="flex items-center gap-2 bg-[#00FFD1] hover:bg-[#00E5BB] text-[#0A1628] px-10 py-4 rounded-lg transition-all font-bold text-base shadow-lg hover:shadow-[#00FFD1]/40 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-up-line text-lg"></i>
              </div>
              Back to Phase Guide
            </a>
            <Link
              to="/terms"
              className="flex items-center gap-2 border-2 border-white/30 text-white hover:border-[#00FFD1] hover:text-[#00FFD1] px-10 py-4 rounded-lg transition-all font-semibold text-base cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-shield-2-fill text-lg"></i>
              </div>
              Read Full Terms
            </Link>
            <button className="flex items-center gap-2 bg-[#00A3E0] hover:bg-[#0088BB] text-white px-10 py-4 rounded-lg transition-all font-bold text-base shadow-lg hover:shadow-[#00A3E0]/40 hover:scale-105 cursor-pointer whitespace-nowrap">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-download-2-fill text-lg"></i>
              </div>
              Download App
            </button>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}